/**
 * Payment terms somebody promised that the contract does not show (ADR 0226,
 * issue #466).
 *
 * The terms-check Edge Function asks Jev, per job, whether the conversation
 * promises the customer a way of paying the payment schedule does not show,
 * and which message does it, and keeps one row per job in
 * `payment_terms_checks`. This decides what the job page and the to-do list
 * say about that row. It warns and never blocks (Jonathon, 2026-09-18, for the
 * quote check; the same rule here).
 *
 * Measured before it was built, on 2026-09-28 against every production job
 * with a sent proposal: the three at 60% or more were all real — #1057 $1k
 * cash on the final payment, #1032 "we'll just pay up front" on a contract
 * that asks nothing until completion, #1047 "being paid by Allstate" while
 * the app sent the customer two $450 reminders — and the next was 39%. So 60%
 * is where it starts to speak, and below 90% it says "looks like", the house
 * rule on confidence (`replyCertainty`).
 */

import { replyCertainty } from "./replyReading.js";
import { shortDayMonth } from "./dates.js";

/** Below this Jev's answer says nothing. */
export const TERMS_WARN_AT = 0.6;

/** A `payment_terms_checks` row, as far as the warning needs it. */
export interface PaymentTermsCheckRow {
  job_id: string;
  fingerprint: string;
  probability: number | string;
  quote: string | null;
  quote_kind: string | null;
  quote_at: string | null;
  dismissed_fingerprint: string | null;
  /** What the warning is about; absent on a row written before ADR 0258. */
  about?: string | null;
  dismissed_about?: string | null;
}

export interface PaymentTermsWarning {
  jobId: string;
  /**
   * What "This is fine" and a tick on the to-do are held against: what the
   * warning is about (ADR 0258), or everything Jev was shown on an older row.
   */
  fingerprint: string;
  /** "Looks like" below 90%. */
  likely: boolean;
  title: string;
  /** Where the quote came from and when: "Email, 24 Sep". */
  source: string | null;
  quote: string | null;
}

const KIND_LABEL: Record<string, string> = {
  email: "Email",
  text: "Text",
  note: "Note",
  call: "Call",
};

/**
 * Whether somebody said "This is fine" about what this row warns of. Held
 * against `about` (the quoted message and the payment schedules, ADR 0258),
 * so new messages elsewhere on the job leave it hidden; a row written before
 * `about` existed falls back to everything Jev was shown.
 */
export function termsDismissed(row: PaymentTermsCheckRow): boolean {
  if (row.about) return row.dismissed_about === row.about;
  return row.dismissed_fingerprint === row.fingerprint;
}

/**
 * The warning a row raises, or null: no row, under 60%, or somebody said
 * "This is fine" about what it warns of.
 */
export function paymentTermsWarning(
  row: PaymentTermsCheckRow | null | undefined,
): PaymentTermsWarning | null {
  if (!row) return null;
  const probability = Number(row.probability);
  if (!Number.isFinite(probability) || probability < TERMS_WARN_AT) {
    return null;
  }
  if (termsDismissed(row)) return null;

  const likely = replyCertainty(probability) !== "stated";
  const when = row.quote_at ? shortDayMonth(row.quote_at) : null;
  const kind = row.quote_kind ? (KIND_LABEL[row.quote_kind] ?? null) : null;
  return {
    jobId: row.job_id,
    fingerprint: row.about || row.fingerprint,
    likely,
    title: likely
      ? "Looks like the customer was promised payment terms the contract doesn’t show."
      : "The customer was promised payment terms the contract doesn’t show.",
    source: row.quote ? [kind, when].filter(Boolean).join(", ") || null : null,
    quote: row.quote,
  };
}
